import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import AiPanel from '@groovehq/internal-design-system/lib/components/Ai/AiPanel'
import AiErrorPanel from '@groovehq/internal-design-system/lib/components/Ai/AiErrorPanel'
import AiGenerating from '@groovehq/internal-design-system/lib/components/Ai/AiGenerating'
import { useAi } from 'ducks/ai/hooks'
import { capture } from 'ducks/tracking/actions'
import { selectCurrentTicketId } from 'selectors/tickets/current/selectCurrentTicketId'
import { selectAccountGlobalId } from 'selectors/app/selectAccountId'
import { buildId } from 'util/globalId'
import { getEditor, isTextSelected, COMPOSER_ID } from 'shared/editor/utils'

import {
  COMPOSER_TYPE,
  TEXT_REFINER_SERVICE,
  SUMMARY_ACTION,
  SPELLING_ACTION,
  SHORTEN_ACTION,
  LENGTHEN_ACTION,
  FORMAL_ACTION,
  CASUAL_ACTION,
  CLARIFY_ACTION,
} from 'ducks/ai/types'
import { getRpcMethodFromActionId } from 'ducks/ai/operations'

const ALLOWED_AI_ACTIONS = [
  SUMMARY_ACTION,
  SUMMARY_ACTION,
  SPELLING_ACTION,
  SHORTEN_ACTION,
  LENGTHEN_ACTION,
  FORMAL_ACTION,
  CASUAL_ACTION,
  CLARIFY_ACTION,
]

const AIFormFooterContentWrapper = ({ children }) => {
  const {
    requestAndWait,
    clearState,
    cancelRequest,
    retryRequest,
    requestState: { loaded, loading, error, aiType, lastAiType },
  } = useAi(COMPOSER_TYPE)
  const ticketId = useSelector(selectCurrentTicketId)
  const accountId = useSelector(selectAccountGlobalId)
  const globalTicketId = buildId('Conversation', ticketId)

  const prepareParams = useCallback(
    id => {
      const editor = getEditor(COMPOSER_ID)
      if (!editor) {
        cancelRequest()
        return false
      }
      const text = isTextSelected(editor)
        ? editor.selection.getContent()
        : editor.getContent()

      switch (id) {
        case SPELLING_ACTION:
          return { text }
        case SHORTEN_ACTION:
          return { length: 'concise', text }
        case LENGTHEN_ACTION:
          return { length: 'verbose', text }
        case FORMAL_ACTION:
          return { tone: 'formal', text }
        case CASUAL_ACTION:
          return { tone: 'casual', text }
        case CLARIFY_ACTION:
          return { text }
        default:
          return {}
      }
    },
    [cancelRequest]
  )

  const updateEditorFromResponse = useCallback(response => {
    const editor = getEditor(COMPOSER_ID)
    if (!response?.result || !editor) {
      return
    }
    editor.undoManager.transact(() => {
      if (isTextSelected(editor)) {
        editor.selection.setContent(response.result)
      } else {
        editor.setContent(response.result)
      }
    })
  }, [])

  const startAiRequest = useCallback(
    async () => {
      capture(`ai ${aiType} request`, {
        ticket_id: globalTicketId,
        account_id: accountId,
      })
      const params = prepareParams(aiType)
      if (!params) {
        return
      }
      const method = getRpcMethodFromActionId(aiType)
      let response
      try {
        response = await requestAndWait({
          service: TEXT_REFINER_SERVICE,
          method,
          params,
        })
      } catch (e) {
        return
      }
      updateEditorFromResponse(response)
    },
    [
      requestAndWait,
      aiType,
      prepareParams,
      updateEditorFromResponse,
      globalTicketId,
      accountId,
    ]
  )

  const handleRetry = useCallback(
    async () => {
      getEditor(COMPOSER_ID).undoManager.undo()
      let response
      try {
        response = await retryRequest()
      } catch (e) {
        return
      }
      updateEditorFromResponse(response)
    },
    [retryRequest, updateEditorFromResponse]
  )

  const handleCancel = useCallback(
    () => {
      if (loading) {
        capture(`ai ${aiType} canceled`, {
          ticket_id: globalTicketId,
          account_id: accountId,
        })
        cancelRequest()
      } else if (loaded && !error) {
        capture(`ai ${lastAiType} abandoned`, {
          ticket_id: globalTicketId,
          account_id: accountId,
        })
        clearState()
        getEditor(COMPOSER_ID).undoManager.undo()
      } else {
        clearState()
      }
    },
    [
      loading,
      loaded,
      error,
      cancelRequest,
      clearState,
      globalTicketId,
      accountId,
      aiType,
      lastAiType,
    ]
  )

  const handleDone = useCallback(
    () => {
      capture(`ai ${aiType} selected`, {
        ticket_id: globalTicketId,
        account_id: accountId,
      })
      clearState()
    },
    [clearState, globalTicketId, accountId, aiType]
  )

  // HACK (jscheel): We should not be triggering the request this way. Just
  // using a useEffect hook. Just going along with the current pattern until we
  // can refactor it.
  useEffect(
    () => {
      if (aiType && ALLOWED_AI_ACTIONS.includes(aiType)) {
        startAiRequest(aiType)
      }
    },
    [aiType, startAiRequest]
  )

  const showAiFooter = loaded || loading || error

  useEffect(
    () => {
      const mode = showAiFooter ? 'readonly' : 'design'
      getEditor(COMPOSER_ID).setMode(mode)
    },
    [showAiFooter]
  )

  return (
    <>
      {!showAiFooter && children}
      {showAiFooter && (
        <>
          {loading && <AiGenerating bare onCancel={handleCancel} />}

          {loaded &&
            !error && (
              <AiPanel
                bare
                onDone={handleDone}
                onRetry={handleRetry}
                onCancel={handleCancel}
              />
            )}
          {error && (
            <AiErrorPanel
              bare
              showSeperator={false}
              onRetry={startAiRequest}
              onCancel={handleCancel}
            />
          )}
        </>
      )}
    </>
  )
}

export default AIFormFooterContentWrapper
